import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ApiSwatidStatusResponse } from '@/types/api';
import { SWATID_MODE_LABELS, type SwatidOperationMode } from '@/constants/swatid';
import { toSwatidMode } from '@/utils/controllerType';

function formatUptime(ms?: number): string | null {
  if (!ms || ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

interface SwatidStatusBarProps {
  controllerStatus: 'online' | 'offline' | 'warning';
  serialNumber?: string;
  statusData?: ApiSwatidStatusResponse;
  lastSeen?: string;
}

export function SwatidStatusBar({
  controllerStatus,
  serialNumber,
  statusData,
  lastSeen,
}: SwatidStatusBarProps) {
  const isOnline = controllerStatus === 'online';
  const mode = statusData?.mode
    ? toSwatidMode(statusData.mode)
    : ('manual' as SwatidOperationMode);
  const cached = statusData?.cached === true;
  const uptime = formatUptime(statusData?.uptimeMs);
  const activeRelays = statusData?.relays?.filter(Boolean).length ?? 0;
  const activeSwitches = statusData?.switches?.filter(Boolean).length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={cn(
            'gap-1.5',
            isOnline ? 'border-success/50 text-success' : 'border-destructive/50 text-destructive'
          )}
        >
          {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
          {isOnline ? 'Online' : 'Offline'}
        </Badge>
        <Badge variant="secondary">SWATID-16</Badge>
        <Badge variant="outline">Modo: {SWATID_MODE_LABELS[mode]}</Badge>
        {statusData?.ip && (
          <Badge variant="outline" className="font-mono text-xs">
            {statusData.ip}
          </Badge>
        )}
        {statusData?.firmware && (
          <Badge variant="outline" className="text-xs">
            FW {statusData.firmware}
          </Badge>
        )}
        {uptime && (
          <Badge variant="outline" className="text-xs">
            Uptime {uptime}
          </Badge>
        )}
        {activeRelays > 0 && (
          <Badge variant="outline" className="text-xs border-success/40 text-success">
            {activeRelays} relé{activeRelays > 1 ? 's' : ''} ON
          </Badge>
        )}
        {activeSwitches > 0 && (
          <Badge variant="outline" className="text-xs">
            {activeSwitches} interruptor{activeSwitches > 1 ? 'es' : ''}
          </Badge>
        )}
        {cached && (
          <Badge variant="outline" className="text-amber-600 border-amber-500/30 text-xs">
            Datos en caché
          </Badge>
        )}
      </div>

      {serialNumber && (
        <p className="text-sm text-muted-foreground font-mono">{serialNumber}</p>
      )}

      {!isOnline && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Dispositivo offline. Control de relés deshabilitado.
            {lastSeen && ` Última conexión: ${new Date(lastSeen).toLocaleString('es-ES')}`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
